// Microsoft Clarity loader. Behavioral analytics: session recordings,
// heatmaps, click maps. Free, no quotas.
//
// Gated by the `analytics` consent bucket (same as GA4). Clarity sets
// 1st-party cookies (`_clck`, `_clsk`, `CLID`) so it falls under GDPR's
// analytics category and must not load before consent.
//
// Lifecycle constraints (mirrors meta-pixel-init.js):
//   - Once injected, Clarity cannot be torn down without a full page
//     reload. So if the user revokes analytics consent mid-session, the
//     SDK keeps running until the next navigation. The next page load
//     will then respect the new (revoked) state and not inject.
//   - Reload-on-grant is therefore unnecessary: we just inject the
//     script the moment consent is granted, and Clarity starts
//     recording the rest of the session immediately.

import { hasAnalyticsConsent, subscribe } from './consent.js';

const PROJECT_ID = import.meta.env.VITE_CLARITY_PROJECT_ID;

let injected = false;

function injectClarity() {
  if (injected) return;
  if (typeof window === 'undefined') return;
  if (!PROJECT_ID) return;
  injected = true;

  // Microsoft's vendor snippet, transposed to module form. Establishes
  // the `clarity` queue function before tag.js loads so any early calls
  // are buffered and replayed once the SDK drains the queue.
  window.clarity = window.clarity || function () {
    (window.clarity.q = window.clarity.q || []).push(arguments);
  };

  const s = document.createElement('script');
  s.async = true;
  s.src = 'https://www.clarity.ms/tag/' + encodeURIComponent(PROJECT_ID);
  const first = document.getElementsByTagName('script')[0];
  if (first && first.parentNode) {
    first.parentNode.insertBefore(s, first);
  } else {
    document.head.appendChild(s);
  }
}

if (typeof window !== 'undefined') {
  if (!PROJECT_ID && import.meta.env.DEV) {
    // eslint-disable-next-line no-console
    console.warn('[crayo] VITE_CLARITY_PROJECT_ID unset; Clarity disabled.');
  }

  if (PROJECT_ID && hasAnalyticsConsent()) {
    injectClarity();
  }

  subscribe(() => {
    if (PROJECT_ID && hasAnalyticsConsent()) injectClarity();
  });
}
